/**
 * Braze Service
 *
 * Self-Registration: Dieser Service registriert sich automatisch beim Import.
 * Consent-Modus: MIT Usercentrics-Consent (requiresConsent: true)
 *
 * Um den Service zu entfernen: Einfach den gesamten braze Ordner löschen
 * und den Import in services/index.js entfernen.
 */
import { registerEventHandler } from "../../../event-registry";
import serviceRegistry from "../ServiceRegistry";
import brazeInteg from "./data-integ";
import brazeProd from "./data-prod";
import { setCards as setContentCards } from "./content-card-store";

export { initBraze, brazeTrackLogin, brazeTrackPurchase, brazeTrackWarenkorbAdd, brazeTrackWarenkorbRemove, brazeTrackMerkzettelAdd, brazeTrackMerkzettelRemove };

function initBraze(dps) {
	const seitentyp = dps.datalayerModel.get('seitentyp');

	if (seitentyp === 'produkt') {
		// Verzögerte Initialisierung nach 5 Sekunden auf Produktseiten
		setTimeout(() => {
			performBrazeInit(dps);
		}, 5000);
	} else {
		// Sofortige Initialisierung auf allen anderen Seiten
		performBrazeInit(dps);
	}
}

function getBrazeScriptUrl() {
	return document.querySelector('meta[name="braze-sdk-url"]')?.content;
}

// Abstrahiert das Bundle-Laden für Testbarkeit
export const _testOnly = {
	importBrazeBundle: (url) => new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = url;
		script.onload = () => resolve(globalThis.braze);
		script.onerror = reject;
		document.head.appendChild(script);
	})
};

async function performBrazeInit(dps) {
	try {
		const scriptUrl = getBrazeScriptUrl();
		if (!scriptUrl) return;

		const mandant = dps.mandantId;
		const { sdkEndpoint, apiKey, showInAppMessages, useServiceWorker } = getBrazeSettingsForMandant(mandant, dps.serviceSettings);

		const { initialize, changeUser, openSession, getUser, subscribeToInAppMessage, showInAppMessage, requestContentCardsRefresh, getCachedContentCards } =
			await _testOnly.importBrazeBundle(scriptUrl);

		const config = {
			baseUrl: sdkEndpoint,
			enableLogging: false
		};
		if (showInAppMessages) {
			config.allowUserSuppliedJavascript = true;
		}
		initialize(apiKey, config);

		// Daten aus dem DataLayer-Model holen (via dps.datalayerModel)
		const hashedEmail = dps.datalayerModel.get('hashed-email');
		if (hashedEmail) {
			changeUser(hashedEmail);
		}

		if (showInAppMessages) {
			subscribeToInAppMessage((inAppMessage) => {
				const triggerId = inAppMessage.triggerId;
				document.body.insertAdjacentHTML('beforeend', `<dl-event name="braze-in-app-message-view" trigger_id="${triggerId}"></dl-event>`);
				showInAppMessage(inAppMessage);
			});
		}

		// Google Consent-Daten aus DataLayer
		const adUserData = dps.datalayerModel.get("2.ad_user_data");
		if (adUserData) {
			getUser().setCustomUserAttribute("$google_ad_user_data", adUserData === "granted");
		}
		const adPersonalization = dps.datalayerModel.get("2.ad_personalization");
		if (adPersonalization) {
			getUser().setCustomUserAttribute("$google_ad_personalization", adPersonalization === "granted");
		}

		// Meta Pixel Consent aus DataLayer
		getUser().setCustomUserAttribute("$meta_pixel", dps.datalayerModel.get("Meta Pixel"));

		openSession();

		// Content Cards laden und den Store füttern, aus dem die <braze-content-card>-
		// Elemente lesen. Wichtig: requestContentCardsRefresh nutzen (nicht
		// subscribeToContentCardsUpdates) — der Success-Callback feuert zuverlässig nach
		// jedem abgeschlossenen Refresh, auch wenn die Cards unverändert sind (z.B. aus
		// einer früheren Session gecached). subscribeToContentCardsUpdates feuert dagegen
		// nur bei *geänderten* Cards und würde gecachte Cards verpassen.
		requestContentCardsRefresh(() => {
			setContentCards(getCachedContentCards().cards);
		});

		if (useServiceWorker && 'serviceWorker' in navigator) {
			navigator.serviceWorker.register('/service-worker.js').catch(() => {});
		}

		// Event-Handler registrieren nach erfolgreicher Initialisierung
		registerBrazeEventHandlers();
	} catch(fehler) {}
}

function registerBrazeEventHandlers() {
	// Event-Handler mit automatischem Replay von gepufferten Events
	// Falls Events vor der Braze-Initialisierung gefeuert wurden (z.B. add_to_cart vor Consent),
	// werden diese automatisch nachträglich abgespielt

	registerEventHandler('login', (model) => {
		brazeTrackLogin(model);
	}, 'braze');

	registerEventHandler('add_to_cart', (model, message) => {
		brazeTrackWarenkorbAdd(message);
	}, 'braze');

	registerEventHandler('remove_from_cart', (model, message) => {
		brazeTrackWarenkorbRemove(message);
	}, 'braze');

	registerEventHandler('add_to_wishlist', (model, message) => {
		brazeTrackMerkzettelAdd(message);
	}, 'braze');

	registerEventHandler('remove_from_wishlist', (model, message) => {
		brazeTrackMerkzettelRemove(message);
	}, 'braze');

	registerEventHandler('purchase', (model) => {
		brazeTrackPurchase(model);
	}, 'braze');

}

function getBrazeSettingsForMandant(mandant, settings) {
	const mandantSettings = settings[mandant];
	return mandantSettings;
}

function brazeTrackPurchase(datalayerModel) {
	// console.log('BRAZE', 'brazeTrackPurchase', datalayerModel);
	//TODO CRM: zusätzliche Attribute definieren
	//TODO CRM: entscheiden, ob auf Produkt- oder Bestellebene getrackt werden soll
	//braze.logPurchase(product_id, price, "USD", quantity, {key: "value"});
}

function brazeTrackLogin(datalayerModel) {
	braze.changeUser(datalayerModel['hashed-email']);
}

function brazeTrackWarenkorbAdd(message) {
	const item = message.ecommerce.items[0];
	const items = { id: item.item_id };
	if (item.shopid) {
		items.shop_id = item.shopid;
	}
	braze.logCustomEvent("add_to_cart", { items });
}

function brazeTrackWarenkorbRemove(message) {
	braze.logCustomEvent("remove_from_cart", {
		items: {
			id: message.ecommerce.items[0].item_id
		}
	});
}

function brazeTrackMerkzettelAdd(message) {
	braze.logCustomEvent("add_to_wishlist", {
		items: {
			id: message.ecommerce.items[0].item_id
		}
	});
}

function brazeTrackMerkzettelRemove(message) {
	braze.logCustomEvent("remove_from_wishlist", {
		items: {
			id: message.ecommerce.items[0].item_id
		}
	});
}

// Self-Registration beim Modulstart
serviceRegistry.registerService({
	name: "Braze",
	initFunction: initBraze,
	requiresConsent: true, // Service benötigt Usercentrics-Consent
	configs: [brazeInteg, brazeProd]
});
